<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast'
  import AutoSavingForm from '@/components/ui/form-ui/AutoSavingForm.vue'
  import { CasesService } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      title: 'Case Notes',
      placeholder: 'Add notes about this case...',
      saveError: 'Failed to update notes',
      template: `Case Summary
-------------------
Client's Objective:
Key Issues:
Important Dates:

Legal Analysis
-------------------
Applicable Laws:
Precedents:
Potential Arguments:

Action Items
-------------------
[ ]
[ ]

Meeting Notes
-------------------
Date:
Attendees:
Key Points:

Next Steps
-------------------
1.
2.

Additional Notes
-------------------`
    },
    de: {
      title: 'Fallnotizen',
      placeholder: 'Fügen Sie Notizen zu diesem Fall hinzu...',
      saveError: 'Fehler beim Aktualisieren der Notizen',
      template: `Fallzusammenfassung
-------------------
Ziel des Mandanten:
Hauptthemen:
Wichtige Termine:

Rechtliche Analyse
-------------------
Anwendbare Gesetze:
Präzedenzfälle:
Mögliche Argumente:

Aufgaben
-------------------
[ ]
[ ]

Besprechungsnotizen
-------------------
Datum:
Teilnehmer:
Kernpunkte:

Nächste Schritte
-------------------
1.
2.

Zusätzliche Notizen
-------------------`
    }
  })

  const caseStore = useCaseStore()
  const { toast } = useToast()

  const formValues = ref({
    notes: ''
  })

  const formMeta = ref({
    valid: true,
    errors: {}
  })

  // Set initial notes or template
  const initializeNotes = () => {
    const notes = caseStore.caseRecord?.note
    if (!notes || notes.trim() === '') {
      formValues.value.notes = t('template')
    } else {
      formValues.value.notes = notes
    }
  }
  initializeNotes()

  const handleSave = async (values: { notes: string }) => {
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseStore.caseRecord?.uuid || '',
        requestBody: {
          note: values.notes
        }
      })
      // Update the store with the new notes
      if (caseStore.caseRecord) {
        caseStore.caseRecord.note = values.notes
      }
    } catch (error) {
      console.error(error)
      toast({
        title: t('saveError'),
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <div class="">
    <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
      <!-- Header -->
      <div class="border-b border-gray-100 px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900">{{ t('title') }}</h2>
      </div>

      <!-- Content Area -->
      <div class="p-6">
        <AutoSavingForm
          :values="formValues"
          :meta="formMeta"
          @save="handleSave"
        >
          <textarea
            v-model="formValues.notes"
            :placeholder="t('placeholder')"
            class="min-h-[500px] w-full rounded-lg border p-4 font-mono text-sm focus:border-transparent
              focus:outline-none focus:ring-2 focus:ring-purple-500"
            style="white-space: pre-wrap"
          />
        </AutoSavingForm>
      </div>
    </div>
  </div>
</template>

<style scoped>
  textarea {
    resize: vertical;
  }
</style>
